/* eslint-disable simple-import-sort/imports */
import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { styled } from 'styled-components';

import { SITE_NAME } from 'constants';
import useLenisScroll from 'hooks/useLenisScroll';
import FooterV2 from 'pages/HomeV2/components/FooterV2';
import Nav from 'pages/HomeV2/components/Nav';
import { LOGO_URL } from 'pages/HomeV2/seo';
import { Grain, v2, V2GlobalStyle } from 'pages/HomeV2/v2';

import GoodieCta from './sections/GoodieCta';
import GoodieFeatures from './sections/GoodieFeatures';
import GoodieHero from './sections/GoodieHero';
import GoodieMoment from './sections/GoodieMoment';
import GoodieTrust from './sections/GoodieTrust';
import GoodieUsp from './sections/GoodieUsp';
import GoodieWorkflow from './sections/GoodieWorkflow';
import {
  GOODIE_DESCRIPTION,
  GOODIE_TITLE,
  GOODIE_URL,
  goodieStructuredData,
} from './seo';
import { GOODIE_EVENTS, trackGoodieEvent } from './tracking';

const NAV_LINKS = [
  { label: 'Funktionen', target: 'features' },
  { label: 'Ablauf', target: 'workflow' },
  { label: 'Demo', target: 'demo' },
];

const Main = styled.main`
  background: ${v2.bg};
  color: ${v2.text};
  overflow-x: clip;
`;

/**
 * /goodie – landing page behind the QR code on the goodie card handed out to
 * dental practices. Built from the start page's design system (pages/HomeV2).
 */
const Goodie = () => {
  const scrollTo = useLenisScroll();
  const pageViewSent = useRef(false);

  useEffect(() => {
    // Ref guard: StrictMode re-runs effects in development.
    if (pageViewSent.current) return;
    pageViewSent.current = true;
    trackGoodieEvent(GOODIE_EVENTS.pageView);
  }, []);

  return (
    <>
      <Helmet>
        <title>{GOODIE_TITLE}</title>
        <meta name="description" content={GOODIE_DESCRIPTION} />
        <link rel="canonical" href={GOODIE_URL} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="de_DE" />
        <meta property="og:site_name" content={SITE_NAME} />
        <meta property="og:url" content={GOODIE_URL} />
        <meta property="og:title" content={GOODIE_TITLE} />
        <meta property="og:description" content={GOODIE_DESCRIPTION} />
        <meta property="og:image" content={LOGO_URL} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={GOODIE_TITLE} />
        <meta name="twitter:description" content={GOODIE_DESCRIPTION} />
        <script type="application/ld+json">
          {JSON.stringify(goodieStructuredData)}
        </script>
      </Helmet>
      <V2GlobalStyle />
      <Grain aria-hidden />
      <Nav onNavigate={scrollTo} links={NAV_LINKS} />
      <Main>
        <GoodieHero onScrollTo={scrollTo} />
        <GoodieMoment />
        <GoodieFeatures />
        <GoodieUsp />
        <GoodieWorkflow />
        <GoodieTrust />
        <GoodieCta />
      </Main>
      <FooterV2 />
    </>
  );
};

export default Goodie;
